<script>
  import {
    documentComputeds,
    documentMethods,
    quoteMethods,
    saleOrderMethods,
    purchaseOrderMethods,
    invoiceMethods,
    quoteActiveKeys,
    salesActiveKeys,
    purchasesActiveKeys,
    invoicesActiveKeys,
    quoteStaleKeys,
    salesStaleKeys,
    purchasesStaleKeys,
    invoicesStaleKeys,
    quoteCompletedKeys,
    salesCompletedKeys,
    purchasesCompletedKeys,
    invoicesCompletedKeys
  } from '@state/helpers'

  export default {
    props: {
      isProject: {
        type: Boolean,
        default: false
      },
      isFinance: {
        type: Boolean,
        default: false
      },
      id: {
        type: Number,
        default: null
      },
      enableQuotes: {
        type: Boolean,
        default: false
      },
      enableSales: {
        type: Boolean,
        default: false
      },
      enablePurchases: {
        type: Boolean,
        default: false
      },
      enableInvoices: {
        type: Boolean,
        default: false
      },
      enableReturns: {
        type: Boolean,
        default: false
      },
      marginless: {
        type: Boolean,
        default: false
      },
    },
    data() {
      return {
        quoteActiveKeys: [...quoteActiveKeys],
        salesActiveKeys: [...salesActiveKeys],
        purchasesActiveKeys: [...purchasesActiveKeys],
        invoicesActiveKeys: [...invoicesActiveKeys],
        isLoading: true,
        rows: [],
      }
    },
    computed:{
      supportsAllocation(){
        return !this.isProject
      },
    },
    async mounted() {
      // mutate activekeys to include `draft`
      this.quoteActiveKeys.push('draft')
      this.salesActiveKeys.push('draft')
      this.purchasesActiveKeys.push('draft')
      this.invoicesActiveKeys.push('draft')
      await this.initRows()
      this.isLoading = false
    },
    methods: {
      ...documentComputeds,
      ...documentMethods,
      ...quoteMethods,
      ...invoiceMethods,
      ...purchaseOrderMethods,
      ...saleOrderMethods,
      async initRows () {
        if (this.isProject) {
          await this.updateProjectDocumentCounts({ projectId: this.id })

          if(this.enableQuotes){
            this.parseResponse('quotes', 'Quotes', this.quoteActiveKeys, quoteStaleKeys)
          }
          if(this.enableSales){
            this.parseResponse('sales', 'Sales Orders', this.salesActiveKeys, salesStaleKeys)
          }
          if(this.enablePurchases){
            this.parseResponse('purchases', 'Purchase Orders', this.purchasesActiveKeys, purchasesStaleKeys)
          }
          if(this.enableInvoices){
            this.parseResponse('invoices', 'Invoices', this.invoicesActiveKeys, invoicesStaleKeys)
          }
          if(this.enableReturns){
          // TBD
          }
        }
        if (this.isFinance || !this.isProject ) {
          await this.updateCustomerDocumentCounts({ customerId: this.id })

          if(this.enableQuotes){
            this.parseResponse('quote', 'Quotes', this.quoteActiveKeys, quoteStaleKeys, quoteCompletedKeys)
          }
          if(this.enableSales){
            this.parseResponse('sale_order', 'Sales Orders', this.salesActiveKeys, salesStaleKeys, salesCompletedKeys)
          }
          if(this.enablePurchases){
            this.parseResponse('purchase', 'Purchase Orders', this.purchasesActiveKeys, purchasesStaleKeys, purchasesCompletedKeys)
          }
          if(this.enableInvoices){
            this.parseResponse('invoice', 'Invoices', this.invoicesActiveKeys, invoicesStaleKeys, invoicesCompletedKeys)
          }
        }
      },
      async parseResponse(name, title, activeKeys, staleKeys, completedKeys){
        let data;
        if (this.isProject) {
          data = (this.getProjectDocumentCounts()(this.id))[name]
        } else if (this.isFinance) {
          data = (this.getFinanceDocumentCounts()(this.id))[name]
        } else if (this.id) {
          data = (this.getCustomerDocumentCounts()(this.id))[name]
        } else {
          return;
        }

        let countActive = 0
        let countStale = 0
        let countComplete = 0

        try {
          for (const [key, value] of Object.entries(data)) {
            if (activeKeys.includes(key)) {
              countActive += value
            } else if (staleKeys.includes(key)) {
              countStale += value
            }
            if (completedKeys && completedKeys.includes(key)) {
              countComplete += value
            }
          }
        } catch(err) {
          countActive = 0
          countStale = 0
          countComplete = 0
        }
        this.rows.push({
          "name": title,
          "active": { "count": countActive, "title": "Open"},
          "stale": {"count": countStale, "title": "On Hold"},
          "complete": {"count": countComplete, "title": "Complete"}
        })
      },
    },
  }
</script>

<template>
  <div
    class="table-status"
    :class="{
      'border': (rows.length > 0),
      'xl:tw-mr-5 xl:tw-mt-10': !marginless,
    }"
  >
    <table class="table-default table-rounded table-striped w-100 table-document">
      <tbody>
        <tr v-for="(item, index) in rows" :key="`row-${index}`" class="f-h-60">
          <td><span class="text-bold text-pill">{{ item.name }}</span></td>
          <td>
            <BaseStatusPill
              class="tw-hidden md:tw-flex"
              is-striped
              :is-striped-alternate="(index % 2) === 1"
              status="active"
              :value="item.active.count + ' ' + item.active.title"
              enable-border />
            <BaseStatusPill
              class="tw-flex md:tw-hidden"
              is-striped
              :is-striped-alternate="(index % 2) === 1"
              status="active"
              :value="item.active.count + ' '"
              enable-border />
          </td>
          <td class="tw-hidden lg:tw-table-cell">
            <BaseStatusPill
              class="tw-hidden md:tw-flex"
              is-striped
              :is-striped-alternate="(index % 2) === 1"
              status="cancelled"
              :value="item.stale.count + ' ' + item.stale.title"
              enable-border />
            <BaseStatusPill
              class="tw-flex md:tw-hidden"
              is-striped
              :is-striped-alternate="(index % 2) === 1"
              status="cancelled"
              :value="item.stale.count + ' '"
              enable-border />
          </td>
          <td v-if="supportsAllocation">
            <BaseStatusPill
              class="tw-hidden md:tw-flex"
              is-striped
              :is-striped-alternate="(index % 2) === 1"
              status="allocated"
              :value="item.complete.count + ' ' + item.complete.title"
              enable-border />
            <BaseStatusPill
              class="tw-flex md:tw-hidden"
              is-striped
              :is-striped-alternate="(index % 2) === 1"
              status="allocated"
              :value="item.complete.count + ' '"
              enable-border />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
